import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Globe, TrendingUp, MapPin } from 'lucide-react';

const Markets = () => {
  const markets = [
    {
      title: 'Africa',
      icon: <MapPin className="w-12 h-12 text-[#263776]" />,
      description: 'Distributor-led lanes with documentation clarity and reliable lead times across multiple African markets.',
      highlights: [
        'Growing distributor networks',
        'Reliable delivery schedules',
        'Market-specific documentation',
        'Bulk supply for institutional buyers'
      ]
    },
    {
      title: 'Asia-Pacific',
      icon: <MapPin className="w-12 h-12 text-[#F47D20]" />,
      description: 'Established lanes serving diverse buyers from manufacturing to retail across the Asia-Pacific region.',
      highlights: [
        'Diverse market coverage',
        'Manufacturing and retail supply',
        'Container and bulk options',
        'Regional logistics optimization'
      ]
    },
    {
      title: 'GCC',
      icon: <MapPin className="w-12 h-12 text-[#263776]" />,
      description: 'Bulk buyers, distributors, retail/private label partners, and HORECA suppliers across Gulf Cooperation Council countries.',
      highlights: [
        'Established import lanes',
        'HORECA and retail distribution',
        'Private label partnerships',
        'Documentation expertise for GCC regulations'
      ]
    },
    {
      title: 'LATAM',
      icon: <MapPin className="w-12 h-12 text-[#F47D20]" />,
      description: 'Growing markets across Latin America with focus on distributor relationships and regional trade expertise.',
      highlights: [
        'Regional market knowledge',
        'Distributor-led growth',
        'Documentation for Latin American markets',
        'Emerging trade lane opportunities'
      ]
    },
    {
      title: 'South Asia',
      icon: <MapPin className="w-12 h-12 text-[#263776]" />,
      description: 'Cross-border supply lanes for institutional buyers, distributors, and food processors.',
      highlights: [
        'Regional trade expertise',
        'Institutional buyer relationships',
        'Processing industry supply',
        'Fast turnaround capabilities'
      ]
    },
    {
      title: 'UK & EU',
      icon: <MapPin className="w-12 h-12 text-[#F47D20]" />,
      description: 'Spec discipline, QA evidence, and documentation readiness for repeatable lanes serving importers and wholesalers.',
      highlights: [
        'EU regulatory compliance',
        'Quality standards adherence',
        'Repeatable sourcing lanes',
        'Distributor and wholesale channels'
      ]
    },
    {
      title: 'US & Canada',
      icon: <MapPin className="w-12 h-12 text-[#263776]" />,
      description: 'North American market serving importers, distributors, and food service providers with stringent quality and compliance standards.',
      highlights: [
        'FDA compliance and documentation',
        'High-volume import lanes',
        'Food service and retail channels',
        'Private label and wholesale distribution'
      ]
    }
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <Globe className="w-20 h-20 text-blue-600" />
            </div>
            <h1 className="text-5xl font-bold mb-6">Markets We Serve</h1>
            <p className="text-xl text-slate-700">
              Built for repeat lanes across high-demand import regions with established documentation and logistics expertise.
            </p>
          </div>
        </div>
      </section>

      {/* Markets Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-12">
            {markets.map((market, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                <div className="grid grid-cols-1 md:grid-cols-3">
                  <div className="bg-blue-50 p-8 flex flex-col items-center justify-center">
                    {market.icon}
                    <h2 className="text-3xl font-bold mt-4">{market.title}</h2>
                  </div>
                  <div className="md:col-span-2 p-8">
                    <p className="text-lg text-slate-700 mb-6">{market.description}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {market.highlights.map((highlight, idx) => (
                        <div key={idx} className="flex items-start">
                          <span className="text-blue-600 mr-2 font-bold">✓</span>
                          <span className="text-slate-700">{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach by Market */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">Our Market Approach</h2>
            <div className="space-y-6">
              <Card className="p-6">
                <div className="flex items-start">
                  <TrendingUp className="w-8 h-8 text-blue-600 mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Market-Specific Documentation</h3>
                    <p className="text-slate-600">
                      We maintain updated checklists for each destination, ensuring compliance with local import regulations, certifications, and documentation requirements.
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-start">
                  <Globe className="w-8 h-8 text-blue-600 mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Logistics Optimization</h3>
                    <p className="text-slate-600">
                      Strategic partnerships with freight forwarders and logistics providers enable reliable delivery schedules and cost-effective routing for each market.
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-start">
                  <MapPin className="w-8 h-8 text-blue-600 mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Local Market Knowledge</h3>
                    <p className="text-slate-600">
                      Understanding of market dynamics, buyer preferences, seasonal trends, and regulatory environments helps us provide better service and advice to our partners.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Tell Us Your Destination */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Tell Us Your Destination</h2>
            <p className="text-lg text-slate-700 mb-8">
              Share your destination, port, and commodity requirement—we'll provide a structured response with documentation checklist and timeline.
            </p>
            <div className="bg-slate-50 p-8 rounded-lg">
              <h3 className="text-xl font-semibold mb-4">What We Need</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                <div className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Commodity type and specification</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Quantity and packaging format</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Destination country and port</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Required certifications</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Delivery timeline expectations</span>
                </div>
                <div className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Incoterms preference</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Build Your Trade Lane?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let us know your market and requirements—we'll provide a detailed supply plan and documentation roadmap.
          </p>
          <div className="flex gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Get a Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white/20">
              <Link to="/products">View Products</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Markets;
